import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  Select,
  Box,
  IconButton,
  FormLabel,
  ListItemText,
  Checkbox,
  OutlinedInput,
  Input,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../../utils/tokenSetCookie";
import { toast } from "react-toastify";
import {
  Country,
  LocationClass,
  LcoationTypeList,
  EditLocationData,
} from "../../../types/types";
import { AxiosError } from "axios";
import LoginRestriction from "../../../utils/loginRestriction";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Add Location Dialog Props
interface AddLocationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  setStatus: (status: string) => void;
  editData: EditLocationData | null;
}

const AddLocationDialog: React.FC<AddLocationDialogProps> = ({
  isOpen,
  onClose,
  mode,
  setStatus,
  editData,
}) => {
  const { t } = useTranslation();
  // Api call state
  const [isCountryName, setCountryName] = useState<Country[]>([]);
  const [isLocationClass, setLocationsClass] = useState<LocationClass[]>([]);
  const [isLocationTypeLists, setLocationTypeLists] = useState<
    LcoationTypeList[]
  >([]);
  const [isRestrictionOpen, setIsRestrictionOpen] = useState(false);

  const [formValues, setFormValues] = useState({
    selectedCountry: "",
    phoneNumber: "",
    state: "",
    streetAddress: "",
    city: "",
    zipcode: "",
    addressType: 0,
    locationName: "",
    selectedLocationClass: [] as string[], // Array of strings
  });
  const initialFormValues = {
    selectedCountry: "",
    phoneNumber: "",
    state: "",
    streetAddress: "",
    city: "",
    zipcode: "",
    addressType: 0,
    locationName: "",
    selectedLocationClass: [] as string[], // Array of strings
  };

  const requiredFieldsFilled =
    formValues.locationName.trim() &&
    formValues.addressType &&
    formValues.selectedCountry.trim() &&
    formValues.phoneNumber.trim() &&
    formValues.state.trim() &&
    formValues.streetAddress.trim() &&
    formValues.city.trim() &&
    formValues.zipcode.trim() !== "";

  // Getting Countries lists
  useEffect(() => {
    const getLocationListDetail = async () => {
      try {
        const response = await axiosInstance.get(API_BASE_URL + "country/list");
        if (response.status >= 200 && response.status <= 300) {
          setCountryName(response.data.data);
        }
      } catch (error) {
        const errCode = error as AxiosError<{ status: number }>;
        if (errCode.response && errCode.response.status === 401) {
          setIsRestrictionOpen(true);
        } else {
          toast.error("Something went wrong", {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
          });
        }
        throw error;
      }
    };
    getLocationListDetail(); // Passing the payload as an argument
  }, []);

  // Getting Location Class lists
  useEffect(() => {
    const getLocationListDetail = async () => {
      try {
        const response = await axiosInstance.get(
          API_BASE_URL + "locationclass/list"
        );
        if (response.status >= 200 && response.status <= 300) {
          setLocationsClass(response.data.data);
        }
      } catch (error) {
        const errCode = error as AxiosError<{ status: number }>;
        if (errCode.response && errCode.response.status === 401) {
          setIsRestrictionOpen(true);
        } else {
          toast.error("Something went wrong", {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
          });
        }
        throw error;
      }
    };
    getLocationListDetail(); // Passing the payload as an argument
  }, []);

  // Getting Location Class lists
  useEffect(() => {
    const getLocationTypeList = async () => {
      try {
        const response = await axiosInstance.get(
          API_BASE_URL + "location-type/list"
        );
        if (response.status >= 200 && response.status <= 300) {
          setLocationTypeLists(response.data.data);
        }
      } catch (error) {
        const errCode = error as AxiosError<{ status: number }>;
        if (errCode.response && errCode.response.status === 401) {
          setIsRestrictionOpen(true);
        } else {
          toast.error("Something went wrong", {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
          });
        }
        throw error;
      }
    };
    getLocationTypeList(); // Passing the payload as an argument
  }, []);


  // Assume editData is an object containing values when editing
  useEffect(() => {
    if (editData) {
      setFormValues({
        selectedCountry: editData.countryCode || "",
        phoneNumber: editData.businessPhone || "",
        state: editData.businessState || "",
        streetAddress: editData.businessStreet1 || "",
        city: editData.businessCity || "",
        zipcode: editData.businessZip || "",
        addressType: editData.businessEntityLocationType || 0, // Assuming this is the correct mapping
        locationName: editData.locationName || "",

        selectedLocationClass: Array.isArray(editData.classId)
          ? editData.classId.map(String) //  Convert to string array
          : typeof editData.classId === "string"
            ? editData.classId.split(",") // Convert comma-separated string to array
            : [], // Default to empty array
      });
    }
  }, [editData]); // Runs when editData changes

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  // Special handler for updating `selectedLocationClass` (array)
  const handleLocationClassChange = (selectedClasses: string[]) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      selectedLocationClass: selectedClasses,
    }));
  };

  // Function to handle form submission
  const handleSubmit = async () => {
    const method = mode === "add" ? "post" : "put";
    const url =
      API_BASE_URL +
      (mode === "add" ? "location/addlocation" : "location/updatelocation"); // Adjust the update endpoint if necessary

    const payload = {
      ...(mode === "edit" && { id: editData?.id }), // Only include `id` if editing
      webSiteId: 1,
      classId: formValues.selectedLocationClass.toString(),
      businessentityLocationName: formValues.locationName,
      businessentityCity: formValues.city,
      businessentityZip: formValues.zipcode,
      businessentityState: formValues.state,
      businessentityPhone: formValues.phoneNumber,
      businessentityStreet1: formValues.streetAddress,
      businessentityLocationType: formValues.addressType,
      countryCode: formValues.selectedCountry,
      locationBrand: "",
    };
    try {
      const response = await axiosInstance[method](url, payload);
      if (response.status >= 200 && response.status <= 300) {
        toast.success(
          mode === "add"
            ? "Location added successfully!"
            : "Location updated successfully!",
          {
            autoClose: 2000,
            position: "top-right",
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          }
        );
        setFormValues(initialFormValues);
        setStatus("true");
        onClose(); //  Close only if request is successful
      }
    } catch (error) {
      const errCode = error as AxiosError<{ status: number }>;
      if (errCode.response && errCode.response.status === 401) {
        setIsRestrictionOpen(true);
      } else {
        toast.error("Something went wrong", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
        });
      }
      throw error;
    }
  };
  const dialogClose = () => {
    setFormValues(initialFormValues);
    onClose();
  };
  const allowOnlyNumbers = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
    ];

    if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData.getData("text");
    if (!/^\d+$/.test(pasteData)) {
      e.preventDefault(); // Block if pasted content is not all digits
    }
  };
  return (
    <>
      <Box sx={{ display: "block", margin: "20px" }}>
        <Dialog open={isOpen} fullWidth maxWidth="md">
          <DialogTitle className="dialogtitle">
            {mode === "add" ? "Add Location" : "Edit Location"}
            <IconButton
              edge="end"
              color="inherit"
              onClick={dialogClose}
              aria-label="close"
              sx={{
                margin: "auto",
                position: "absolute",
                right: "25px",
                top: "17px",
                border: "1px solid #be1e2d", // Red border
                color: "#be1e2d", // Default text color
                "&:hover": {
                  backgroundColor: "#be1e2d", // Full red background on hover
                  color: "#ffffff", // White icon color
                },
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ padding: 2 }} className="addUser">
              <Grid item xs={12} md={6}>
                <FormLabel className="addUser_formlabel">
                  {t("business_entity")}
                </FormLabel>

                <TextField
                  label="Clinical test"
                  size="small"
                  variant="outlined"
                  fullWidth
                  sx={{
                    backgroundColor: "#f0f2f4",
                    cursor: "not-allowed", // Set the cursor to "not-allowed"
                  }}
                  InputProps={{
                    readOnly: true, // Prevents text entry
                    style: {
                      pointerEvents: "none", // Disables interaction with the field
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ marginTop: "10px" }}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  }}
                >
                  <FormLabel className="addUser_formlabel" required>
                    {t("address_type")}
                  </FormLabel>

                  <Select
                    name="addressType" // Ensure it has a name attribute for dynamic updates
                    value={formValues.addressType} // Bind state
                    onChange={(e) =>
                      setFormValues((prevValues) => ({
                        ...prevValues,
                        addressType: Number(e.target.value), // Directly assign the selected value
                      }))
                    }
                    // disableUnderline
                    size="small"
                    displayEmpty
                    renderValue={(selected) =>
                      selected
                        ? isLocationTypeLists.find(
                            (type) => type.id === Number(selected)
                          )?.locationType || "Select Address Type"
                        : "Select Address Type"
                    }
                    sx={{
                      border: "1px solid lightgrey",
                      borderRadius: "4px",
                      "&:focus": {
                        border: "1px solid lightgrey",
                        outline: "none",
                      },
                      "&.Mui-focused": {
                        border: "1px solid lightgrey",
                        outline: "none",
                      },
                      "& .MuiInputBase-input": {
                        color: "black",
                      },
                    }}
                  >
                    {isLocationTypeLists.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.locationType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormLabel className="addUser_formlabel" required>
                  {t("location_name")}
                </FormLabel>
                <TextField
                  name="locationName"
                  size="small"
                  value={formValues.locationName}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined" // Use outlined variant for border support
                  InputProps={{
                    sx: {
                      border: "1px solid lightgrey",
                      borderRadius: "4px",
                      padding: "2px",
                      "&.Mui-focused": {
                        border: "1px solid lightgrey",
                        outline: "none",
                      },
                      "& .MuiInputBase-input": {
                        color: "black",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormLabel className="addUser_formlabel">
                  {t("location_class_name")}
                </FormLabel>
                <Select
                  multiple
                  size="small"
                  value={formValues.selectedLocationClass}
                  onChange={(e) => {
                    const value = e.target.value;
                    const selectedClasses = Array.isArray(value)
                      ? value.map(String)
                      : [String(value)];
                    handleLocationClassChange(selectedClasses);
                  }}
                  input={<OutlinedInput />}
                  renderValue={
                    (selected) =>
                      selected
                        .map(
                          (id) =>
                            isLocationClass.find((loc) => String(loc.id) === id)
                              ?.className
                        )
                        .filter(Boolean)
                        .join(", ") // Show class names in the dropdown
                  }
                  fullWidth
                >
                  {/* Regular Options */}
                  {isLocationClass.map((location) => (
                    <MenuItem key={location.id} value={String(location.id)}>
                      <Checkbox
                        checked={formValues.selectedLocationClass.includes(
                          String(location.id)
                        )}
                      />
                      <ListItemText primary={location.className} />
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  }}
                >
                  <FormLabel className="addUser_formlabel" required>
                    {t("country")}
                  </FormLabel>

                  <Select
                    name="countryCode"
                    value={formValues.selectedCountry} // Replace with your state variable for country selection
                    onChange={(e) =>
                      setFormValues((prevValues) => ({
                        ...prevValues,
                        selectedCountry: e.target.value, // Store only countryCode
                      }))
                    }
                    // disableUnderline
                    size="small"
                    displayEmpty
                    renderValue={(selected) => {
                      const selectedCountry = isCountryName.find(
                        (c) => c.countryCode === selected
                      );
                      return selectedCountry ? (
                        selectedCountry.countryName
                      ) : (
                        <>{t("please_select_country")}</>
                      );
                    }}
                    sx={{
                      border: "1px solid lightgrey",
                      borderRadius: "4px",
                      padding: "2px",
                      "&:focus": {
                        border: "1px solid lightgrey",
                        outline: "none",
                      },
                      "&.Mui-focused": {
                        border: "1px solid lightgrey",
                        outline: "none",
                      },
                      "& .MuiInputBase-input": {
                        color: "black",
                      },
                      "&::after": {
                        borderBottom: "2px solid lightgrey",
                      },
                    }}
                  >
                    {isCountryName.map((country) => (
                      <MenuItem
                        key={country.countryCode}
                        value={country.countryCode}
                      >
                        {country.countryName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormLabel className="addUser_formlabel" required>
                  {t("telephone_number")}
                </FormLabel>
                <Input
                  name="phoneNumber"
                  size="small"
                  value={formValues.phoneNumber}
                  onKeyPress={allowOnlyNumbers}
                  onPaste={handlePaste}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{
                    border: "1px solid lightgrey",
                    borderRadius: "4px",
                    padding: "5px 14px",
                    "&:focus": {
                      border: "1px solid lightgrey",
                      outline: "none",
                    },
                    "&.Mui-focused": {
                      border: "1px solid lightgrey",
                      outline: "none",
                    },
                    "& .MuiInputBase-input": {
                      color: "black",
                    },
                    "&::after": {
                      borderBottom: "2px solid lightgrey",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormLabel className="addUser_formlabel" required>
                  {t("state_province")}
                </FormLabel>

                <Input
                  name="state"
                  size="small"
                  value={formValues.state}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{
                    border: "1px solid lightgrey",
                    borderRadius: "4px",
                    padding: "5px 14px",
                    "&:focus": {
                      border: "1px solid lightgrey",
                      outline: "none",
                    },
                    "&.Mui-focused": {
                      border: "1px solid lightgrey",
                      outline: "none",
                    },
                    "& .MuiInputBase-input": {
                      color: "black",
                    },
                    "&::after": {
                      borderBottom: "2px solid lightgrey",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormLabel className="addUser_formlabel" required>
                  {t("street_address")}
                </FormLabel>

                <Input
                  name="streetAddress"
                  size="small"
                  value={formValues.streetAddress}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{
                    border: "1px solid lightgrey",
                    borderRadius: "4px",
                    padding: "5px 14px",
                    "&:focus": {
                      border: "1px solid lightgrey",
                      outline: "none",
                    },
                    "&.Mui-focused": {
                      border: "1px solid lightgrey",
                      outline: "none",
                    },
                    "& .MuiInputBase-input": {
                      color: "black",
                    },
                    "&::after": {
                      borderBottom: "2px solid lightgrey",
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormLabel className="addUser_formlabel" required>
                  {t("city")}
                </FormLabel>

                <Input
                  name="city"
                  size="small"
                  value={formValues.city}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{
                    border: "1px solid lightgrey",
                    borderRadius: "4px",
                    padding: "5px 14px",
                    "&:focus": {
                      border: "1px solid lightgrey",
                      outline: "none",
                    },
                    "&.Mui-focused": {
                      border: "1px solid lightgrey",
                      outline: "none",
                    },
                    "& .MuiInputBase-input": {
                      color: "black",
                    },
                    "&::after": {
                      borderBottom: "2px solid lightgrey",
                    },
                  }}
                />
              </Grid>{" "}
              <Grid item xs={12} md={6}>
                <FormLabel className="addUser_formlabel" required>
                  {t("zipcode")}
                </FormLabel>

                <Input
                  name="zipcode"
                  size="small"
                  value={formValues.zipcode}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{
                    border: "1px solid lightgrey",
                    borderRadius: "4px",
                    padding: "5px 14px",
                    "&:focus": {
                      border: "1px solid lightgrey",
                      outline: "none",
                    },
                    "&.Mui-focused": {
                      border: "1px solid lightgrey",
                      outline: "none",
                    },
                    "& .MuiInputBase-input": {
                      color: "black",
                    },
                    "&::after": {
                      borderBottom: "2px solid lightgrey",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormLabel className="addUser_formlabel" required>
                  {t("total_num_of_location_allowed")}
                </FormLabel>

                <TextField
                  label="10"
                  size="small"
                  variant="outlined"
                  fullWidth
                  sx={{
                    backgroundColor: "#f0f2f4",
                    cursor: "not-allowed", // Set the cursor to "not-allowed"
                  }}
                  InputProps={{
                    readOnly: true, // Prevents text entry
                    style: {
                      pointerEvents: "none", // Disables interaction with the field
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormLabel className="addUser_formlabel" required>
                  {t("num_of_locations_currently_registered")}
                </FormLabel>

                <TextField
                  label="3"
                  size="small"
                  variant="outlined"
                  fullWidth
                  sx={{
                    backgroundColor: "#f0f2f4",
                    cursor: "not-allowed", // Set the cursor to "not-allowed"
                  }}
                  InputProps={{
                    readOnly: true, // Prevents text entry
                    style: {
                      pointerEvents: "none", // Disables interaction with the field
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={dialogClose}
              className="cancelbtn"
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              className="addbtn"
              sx={{
                // height: "40px",
                cursor: !requiredFieldsFilled ? "not-allowed" : "pointer",
                opacity: !requiredFieldsFilled ? 0.9 : 1,
                pointerEvents: !requiredFieldsFilled ? "none" : "auto",
              }}
              disabled={!requiredFieldsFilled}
            >
              {mode === t("add") ? t("add") : t("save")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      <LoginRestriction open={isRestrictionOpen} />
    </>
  );
};
export default AddLocationDialog;
